import { cloneDeep, compact, orderBy, uniq } from 'lodash';
import { createContext, useEffect, useState } from 'react';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import {
  description,
  initialProvidersState,
  objectivesList,
  Provider,
} from './descriptions';

interface Onboarding {
  localStorageKey: string;
  objectives: number[];
  setObjectives: React.Dispatch<React.SetStateAction<number[]>>;
  jobTitle: number | undefined;
  setJobTitle: React.Dispatch<React.SetStateAction<number | undefined>>;
  teamSize: number | undefined;
  setTeamSize: React.Dispatch<React.SetStateAction<number | undefined>>;
  channelVia: number[];
  setChannelVia: React.Dispatch<React.SetStateAction<number[]>>;
  providers: Provider[];
  initialProvidersState: Provider[];
  setProviders: React.Dispatch<React.SetStateAction<Provider[]>>;
  segmentsMatchingObjectives: string[];
  segments: string[];
  setSegments: React.Dispatch<React.SetStateAction<string[]>>;
  getSegmentDescription: (segment: string) => string;
  providerSearch: string;
  setProviderSearch: React.Dispatch<React.SetStateAction<string>>;
  filteredProviders: Provider[];
  categoryFilter: string;
  setCategoryFilter: React.Dispatch<React.SetStateAction<string>>;
}
export const OnboardingContext = createContext({} as Onboarding);

export const OnboardingContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [objectives, setObjectives] = useState<number[]>([]);
  const [jobTitle, setJobTitle] = useState<number | undefined>();
  const [teamSize, setTeamSize] = useState<number | undefined>();
  const [channelVia, setChannelVia] = useState<number[]>([]);

  const [segments, setSegments] = useState<string[]>([]);

  const [providers, setProviders] = useState(initialProvidersState);
  const [providerSearch, setProviderSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('recomended');

  // If changing this need to also change the integrations page redirect
  const localStorageKey = 'segments_onboarding';

  const segmentsMatchingObjectives = (() => {
    if (objectives.length === 0) {
      return ['Active loyals', 'Loyals', 'High spenders'];
    }

    return compact(
      uniq(
        objectives.flatMap((objective) => {
          return objectivesList.find((o) => o.value === objective)?.segments;
        })
      )
    );
  })();

  const getSegmentDescription = (segment: string) => {
    if (description[segment]) {
      return description[segment];
    }

    return 'No description available';
  };

  const { rawQueryParams } = useParseURLQueryParams();

  const queryParams = `store_name=${rawQueryParams?.store_name}`;

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!rawQueryParams?.store_name,
  });

  useEffect(() => {
    if (storeInfo) {
      if (storeInfo.integrations.length > 0) {
        const updatedProviders = cloneDeep(initialProvidersState);

        const savedProgress = JSON.parse(
          localStorage.getItem(localStorageKey) || '{}'
        );

        storeInfo.integrations.forEach((integration) => {
          const providerIndex = updatedProviders.findIndex(
            (provider) => provider.name === integration.provider
          );

          if (providerIndex !== -1) {
            updatedProviders[providerIndex].connected = true;
            updatedProviders[providerIndex].auto_sync =
              savedProgress?.integrations?.includes(integration.provider);
          }
        });

        setProviders(updatedProviders);
      } else {
        setProviders(initialProvidersState);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeInfo?.integrations]);

  const filteredProviders = (() => {
    if (providerSearch !== '') {
      return orderBy(
        providers.filter((provider) =>
          provider.name.toLowerCase().includes(providerSearch.toLowerCase())
        ),
        'connected',
        'desc'
      );
    }

    switch (categoryFilter) {
      case 'recomended':
        return orderBy(
          providers.filter((provider) => provider.recomended),
          'connected',
          'desc'
        );
      case 'all':
        return orderBy(providers, 'connected', 'desc');
      default:
        return orderBy(
          providers.filter((provider) => provider.category === categoryFilter),
          'connected',
          'desc'
        );
    }
  })();

  return (
    <OnboardingContext.Provider
      value={{
        objectives,
        setObjectives,
        channelVia,
        jobTitle,
        setChannelVia,
        setJobTitle,
        setTeamSize,
        teamSize,
        localStorageKey,
        providers,
        setProviders,
        initialProvidersState,
        segmentsMatchingObjectives,
        getSegmentDescription,
        filteredProviders,
        providerSearch,
        setProviderSearch,
        categoryFilter,
        setCategoryFilter,
        segments,
        setSegments,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
